/* istanbul ignore file */
import React, { useCallback } from 'react';
import classnames from 'classnames';
import { ButtonDropdownLayoutProps, ButtonDropdownLayoutType } from 'framework/form';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuList from '@mui/material/MenuList';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import ArrowIcon from '@mui/icons-material/ArrowDropDownOutlined';
import { dropdownMenuPaperSx } from 'framework/ui/styles';

const ButtonDropdownLayout: ButtonDropdownLayoutType = ({
  label,
  children,
  displayedValue,
  onRemoveCallback,
  className,
  toggleMenu,
  isOpen,
  shouldCloseMenuOnReset,
  source,
  popperPlacement,
}: ButtonDropdownLayoutProps) => {
  const id = source || label?.toString() || '';
  let dropdownToggleLabel: any = label;
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const isFilterDropdown = className?.includes('filter-input') || className?.includes('quick-filter-popper');

  if (displayedValue) {
    dropdownToggleLabel = (
      <>
        {typeof label === 'string' ? (
          <Typography variant="body1" sx={{ mr: 0.5 }}>
            {`${label}:`}
          </Typography>
        ) : (
          label
        )}

        <Typography variant="subtitle1">{displayedValue}</Typography>
      </>
    );
  }

  const onRemoveButtonClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (shouldCloseMenuOnReset && isOpen) {
        toggleMenu();
      }

      if (onRemoveCallback) {
        onRemoveCallback();
      }
    },
    [onRemoveCallback, isOpen, shouldCloseMenuOnReset, toggleMenu],
  );

  return (
    <>
      <Button
        ref={anchorRef}
        data-testid="toggle-btn"
        id={id}
        aria-controls={isOpen ? id : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen ? 'true' : undefined}
        onClick={toggleMenu}
        variant="outlined"
        className={classnames('button-dropdown__toggle', { 'button-dropdown__toggle--active': !!displayedValue })}
        sx={{
          color: (theme: any) => theme.vars.palette.text.primary,
          paddingTop: '2px',
          paddingBottom: '2px',
          paddingLeft: '7px',
          paddingRight: onRemoveCallback ? '2px' : '7px',
          opacity: 0.8,
          b: { marginLeft: '5px' },
          '& > span': { whiteSpace: 'nowrap' },
          '& .filter-dropdown-counter': {
            backgroundColor: 'primary.main',
            borderRadius: '4px',
            padding: '4px',
            color: 'common.white',
            fontSize: '10px',
            lineHeight: '10px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: 'translateY(-2px)',
            marginLeft: '4px',
            marginRight: '-3px',
          },
          '&.button-dropdown__toggle--active': {
            backgroundColor: 'rgba(var(--mui-palette-primary-mainChannel) / var(--mui-palette-action-hoverOpacity))',
            border: '1px solid',
            borderColor: 'primary.main',
            color: 'primary.main',
            opacity: 1,
            '& svg': { color: 'primary.main' },
          },
        }}
      >
        {dropdownToggleLabel}
        <ArrowIcon fontSize="small" sx={{ marginLeft: '9px', fontSize: '22px' }} />
        {!!onRemoveCallback && (
          <IconButton data-testid="remove-btn" size="small" onClick={onRemoveButtonClick} sx={{ marginLeft: '5px' }}>
            <CloseIcon sx={{ fontSize: '15px', color: (theme: any) => theme.vars.palette.text.primary }} />
          </IconButton>
        )}
      </Button>
      <Popper
        open={isOpen}
        anchorEl={anchorRef.current}
        placement={popperPlacement || 'bottom-start'}
        transition
        disablePortal
        className={className}
        sx={{ zIndex: 1200 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper
              elevation={3}
              className="button-dropdown__menu"
              sx={theme => ({
                ...dropdownMenuPaperSx,
                '& .button-dropdown__menu-header': {
                  padding: '6px 16px 4px',
                  fontWeight: 600,
                  '&:not(:first-child)': { marginTop: '13px' },
                  '&:not(.multiple)': { paddingLeft: '12px', paddingRight: '12px' },
                },
                [theme.breakpoints.down('sm')]: {
                  '& .button-dropdown__menu-item': { minHeight: '34px' },
                },
                ...(isFilterDropdown
                  ? {
                      '& .react-autosuggest__suggestion, & .button-dropdown__menu-item': {
                        fontFamily: theme.typography.fontFamily,
                        width: '100%',
                        textAlign: 'left',
                        color: 'text.primary',
                        border: 0,
                        padding: '5px 12px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        minWidth: '200px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        backgroundColor: 'background.paper',
                        minHeight: '34px',
                        borderRadius: 0,
                        [theme.breakpoints.down('sm')]: { fontSize: '14px' },
                        '&:disabled, &:hover, &:focus': {
                          backgroundColor: 'common.menuItemActive',
                          color: 'text.primary',
                          opacity: 1,
                          textDecoration: 'none',
                        },
                        '&:disabled': { backgroundColor: 'common.inputDisabledBackground !important' },
                        '&.multiple:first-child': { marginTop: '10px' },
                      },
                    }
                  : {}),
              })}
            >
              <ClickAwayListener onClickAway={toggleMenu} touchEvent={false}>
                <MenuList autoFocusItem={false} id={id}>
                  {children}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default ButtonDropdownLayout;
